import { Injectable, inject } from '@angular/core';

import { combineLatest, filter, map, mergeMap, of, switchMap, tap } from 'rxjs';

import {
  CamBaseService,
  GraphEndpoint,
  HandleComplexRequest,
  HandleSimpleRequest,
  graphQlUpdateFields,
} from '@camelot/server';
import { CamUsersService } from '@camelot/user';
import { isNonNullable } from '@camelot/utils';

import { Quotation, quotationProps } from './dto/quotation';
import { QuotationCreationPayloadInput } from './dto/quotation-creation-payload-input';
import { QuotationModifierPayloadInput } from './dto/quotation-modifier-payload-input';
import { QuotationSignature } from './dto/quotation-signature';
import { QuotationVersionCreationPayloadInput } from './dto/quotation-version-creation-payload-input';
import { QuotationVersionModifierPayloadInput } from './dto/quotation-version-modifier-payload-input';
import { RefusalInfos } from './dto/refusal-infos';
import { QuotationRow, quotationRowFields, quotationRowProps } from './dto/row';
import { QuotationVersion, QuotationVersionExtended, quotationVersionProps } from './dto/version';
import {
  CREATE_QUOTATION,
  CREATE_QUOTATION_VERSION,
  QUOTATION_VERSION_REFUSED,
  QUOTATION_VERSION_SIGNED,
  READ_QUOTATION_VERSION,
  UPDATE_QUOTATION,
  UPDATE_QUOTATION_VERSION,
} from './mutations';
import {
  GET_QUOTATIONS,
  GET_QUOTATION_ROWS,
  GET_QUOTATION_SIGNATURE_LINK,
  GET_QUOTATION_VERSIONS,
  GET_QUOTATION_VERSIONS_BY_QUOTATION,
  GET_QUOTATION_VERSION_BY_ID,
  GET_VERSION_TENANT_ROUTE,
} from './queries';
import { IQuotationsService } from './quotations-service.interface';

const quotationRowSelection = quotationRowFields.join(' ');

const graphEndpoint: GraphEndpoint = {
  clientName: 'quotationService',
  endpoint: 'quotation',
};

@Injectable({
  providedIn: 'root',
})
export class CamQuotationsService extends CamBaseService implements IQuotationsService {
  protected _graphEndpoint = graphEndpoint;
  public quotationsByProject = new HandleComplexRequest<Quotation[]>();
  public quotationsByContact = new HandleComplexRequest<Quotation[]>();
  public quotations = new HandleSimpleRequest<Quotation[]>();
  public quotation = new HandleComplexRequest<Quotation>();
  public quotationRows = new HandleComplexRequest<QuotationRow[]>();
  public quotationParentRows = new HandleComplexRequest<QuotationRow[]>();
  public quotationSignature = new HandleComplexRequest<QuotationSignature>();

  public version = new HandleComplexRequest<QuotationVersionExtended>();
  public versions = new HandleSimpleRequest<QuotationVersion[]>();
  public versionsByQuotation = new HandleComplexRequest<QuotationVersion[]>();
  public versionsByContact = new HandleComplexRequest<QuotationVersion[]>();
  public versionsByProject = new HandleComplexRequest<QuotationVersion[]>();

  public tenantRoute = new HandleSimpleRequest<string>();

  private _usersService = inject(CamUsersService);

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchQuotationsByProject$(projectId: string) {
    return this.quotationsByProject.fetch(
      projectId,
      this._graphService
        .fetchPagedQueryList<Quotation>(
          GET_QUOTATIONS(
            `where: { projectId: { eq: "${projectId}" } }`,
            `
          ${quotationProps.get('id')}
          ${quotationProps.get('name')}
          ${quotationProps.get('projectId')}
          ${quotationProps.get('tenantQuotationId')}
          ${quotationProps.get('status')}
          ${quotationProps.get('dueDate')}
          ${quotationProps.get('exclVatTotal')}
          ${quotationProps.get('inclVatTotal')}
          ${quotationProps.get('rowsCount')}
          ${quotationProps.get('rowsMigratedCount')}
          ${quotationProps.get('versions')} {
            ${quotationVersionProps.get('id')}
            ${quotationVersionProps.get('version')}
            ${quotationVersionProps.get('status')}
            ${quotationVersionProps.get('dueDate')}
            ${quotationVersionProps.get('acceptanceDate')}
            ${quotationVersionProps.get('exclVatTotal')}
            ${quotationVersionProps.get('inclVatTotal')}
            ${quotationVersionProps.get('isNew')}
          }
          `
          ),
          'filteredQuotations',
          this._graphEndpoint.clientName
        )
        .pipe(map(data => data.items ?? []))
    );
  }

  public fetchQuotationsByContact$(contactId: string) {
    return this.quotationsByContact.fetch(
      contactId,
      this._graphService
        .fetchPagedQueryList<Quotation>(
          GET_QUOTATIONS(
            `where: { contactId: { eq: "${contactId}" } }`,
            `
            ${quotationProps.get('id')}
            ${quotationProps.get('name')}
            ${quotationProps.get('contactId')}
          `
          ),
          'filteredQuotations',
          this._graphEndpoint.clientName
        )
        .pipe(map(data => data.items ?? []))
    );
  }

  public fetchQuotations$() {
    return this.quotations.fetch(
      this._graphService
        .fetchPagedQueryList<Quotation>(
          GET_QUOTATIONS(
            ``,
            `
            ${quotationProps.get('id')}
            ${quotationProps.get('name')}
            ${quotationProps.get('contactId')}
            ${quotationProps.get('projectId')}
          `
          ),
          'filteredQuotations',
          this._graphEndpoint.clientName
        )
        .pipe(map(data => data.items ?? []))
    );
  }

  public fetchQuotation$(id: string) {
    return this.quotation.fetch(
      id,
      this._graphService
        .fetchPagedQueryList<Quotation>(
          GET_QUOTATIONS(
            `where: { id: { eq: "${id}" } }`,
            `
            ${quotationProps.get('id')}
            ${quotationProps.get('name')}
            ${quotationProps.get('versions')} {
              ${quotationVersionProps.get('id')}
              ${quotationVersionProps.get('version')}
              ${quotationVersionProps.get('status')}
              ${quotationVersionProps.get('isNew')}
              ${quotationVersionProps.get('date')}
              ${quotationVersionProps.get('devisorId')}
            }
            `
          ),
          'filteredQuotations',
          this._graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items),
          filter(isNonNullable),
          filter(items => items.length > 0),
          map(items => items[0])
        )
    );
  }

  public fetchQuotationRows$(versionId: string) {
    return this.quotationRows.fetch(
      versionId,
      this._graphService.fetchQuery<QuotationRow[]>(
        GET_QUOTATION_ROWS(
          `where: { documentIds: { any: true }, quotationVersionId: { eq: "${versionId}" } }`,
          `
            ${quotationRowProps.get('id')}
            ${quotationRowProps.get('description')}
            ${quotationRowProps.get('documentIds')}
          `
        ),
        'filteredQuotationRows',
        this._graphEndpoint.clientName
      )
    );
  }

  public fetchQuotationVersionsByContact$(contactId: string) {
    return this.versionsByContact.fetch(
      contactId,
      this._graphService
        .fetchPagedQueryList<QuotationVersion>(
          GET_QUOTATION_VERSIONS(
            `where: { quotation: { contactId: { eq: "${contactId}" } } }`,
            `
          ${quotationVersionProps.get('id')}
          ${quotationVersionProps.get('version')}
          ${quotationVersionProps.get('status')}
          ${quotationVersionProps.get('date')}
          ${quotationVersionProps.get('devisorId')}
          ${quotationVersionProps.get('exclVatTotal')}
          ${quotationVersionProps.get('inclVatTotal')}
          ${quotationVersionProps.get('quotation')} {
            ${quotationProps.get('name')}
            ${quotationProps.get('projectId')}
          }
          `
          ),
          'quotationVersions',
          this._graphEndpoint.clientName
        )
        .pipe(map(data => data.items ?? []))
    );
  }

  public fetchQuotationVersionsByProject$(projectId: string) {
    return this.versionsByProject.fetch(
      projectId,
      this._graphService
        .fetchPagedQueryList<QuotationVersion>(
          GET_QUOTATION_VERSIONS(
            `where: { quotation: { projectId: { eq: "${projectId}" } } }`,
            `
          ${quotationVersionProps.get('id')}
          ${quotationVersionProps.get('version')}
          ${quotationVersionProps.get('status')}
          ${quotationVersionProps.get('date')}
          ${quotationVersionProps.get('devisorId')}
          ${quotationVersionProps.get('exclVatTotal')}
          ${quotationVersionProps.get('inclVatTotal')}
          ${quotationVersionProps.get('quotation')} {
            ${quotationProps.get('name')}
            ${quotationProps.get('projectId')}
          }
          `
          ),
          'quotationVersions',
          this._graphEndpoint.clientName
        )
        .pipe(map(data => data.items ?? []))
    );
  }

  public fetchQuotationVersions$() {
    return this.versions.fetch(
      this._graphService
        .fetchPagedQueryList<QuotationVersion>(
          GET_QUOTATION_VERSIONS(
            ``,
            `
            ${quotationVersionProps.get('id')}
            ${quotationVersionProps.get('version')}
            ${quotationVersionProps.get('status')}
            ${quotationVersionProps.get('description')}
            ${quotationVersionProps.get('date')}
            ${quotationVersionProps.get('dueDate')}
            ${quotationVersionProps.get('acceptanceDate')}
            ${quotationVersionProps.get('paymentTerm')}
            ${quotationVersionProps.get('downloadUriPDF')}
            ${quotationVersionProps.get('downloadUriExcel')}
            ${quotationVersionProps.get('devisorId')}
            ${quotationVersionProps.get('rowsCount')}
            ${quotationVersionProps.get('rowsMigratedCount')}
            ${quotationVersionProps.get('fullyMigrated')}
            ${quotationVersionProps.get('exclVatTotal')}
            ${quotationVersionProps.get('inclVatTotal')}
            ${quotationVersionProps.get('discount')}
            ${quotationVersionProps.get('isNew')}
            ${quotationVersionProps.get('canBeSigned')}
            ${quotationVersionProps.get('quotation')} {
              ${quotationProps.get('id')}
              ${quotationProps.get('name')}
              ${quotationProps.get('tenantQuotationId')}
            }
          `
          ),
          'quotationVersions',
          this._graphEndpoint.clientName
        )
        .pipe(map(data => data.items ?? []))
    );
  }

  public fetchVersions$(ids: string[]) {
    return combineLatest(ids.map(id => this.fetchQuotationVersion$(id)));
  }

  public fetchQuotationVersion$(id: string) {
    return this.version.fetch(
      id,
      this._graphService
        .fetchPagedQueryList<QuotationVersionExtended>(
          GET_QUOTATION_VERSIONS(
            `where: { id: { eq: "${id}" }}`,
            `
            ${quotationVersionProps.get('id')}
            ${quotationVersionProps.get('version')}
            ${quotationVersionProps.get('status')}
            ${quotationVersionProps.get('description')}
            ${quotationVersionProps.get('date')}
            ${quotationVersionProps.get('dueDate')}
            ${quotationVersionProps.get('acceptanceDate')}
            ${quotationVersionProps.get('paymentTerm')}
            ${quotationVersionProps.get('vatLevelId')}
            ${quotationVersionProps.get('hideRowsPrice')}
            ${quotationVersionProps.get('downloadUriPDF')}
            ${quotationVersionProps.get('downloadUriExcel')}
            ${quotationVersionProps.get('devisorId')}
            ${quotationVersionProps.get('rowsCount')}
            ${quotationVersionProps.get('rowsMigratedCount')}
            ${quotationVersionProps.get('fullyMigrated')}
            ${quotationVersionProps.get('exclVatTotal')}
            ${quotationVersionProps.get('inclVatTotal')}
            ${quotationVersionProps.get('discount')}
            ${quotationVersionProps.get('isNew')}
            ${quotationVersionProps.get('canBeSigned')}
            ${quotationVersionProps.get('documentIds')}
            ${quotationVersionProps.get('quotation')} {
              ${quotationProps.get('id')}
              ${quotationProps.get('name')}
              ${quotationProps.get('projectId')}
              ${quotationProps.get('contactId')}
            }
          `
          ),
          'quotationVersions',
          this._graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => (data.items ? data.items[0] : null)),
          filter(isNonNullable),
          mergeMap(entity => {
            if (entity.devisorId === null) {
              return of(entity);
            }
            return this._usersService.fetchUser$(entity.devisorId).pipe(
              map(data => ({
                ...entity,
                ...{ devisor: data },
              }))
            );
          })
        )
    );
  }

  public fetchQuotationVersionById$(id: string) {
    return this.version.fetch(
      id,
      this._graphService
        .fetchQuery<QuotationVersionExtended>(
          GET_QUOTATION_VERSION_BY_ID(
            id,
            `
            ${quotationVersionProps.get('id')}
            ${quotationVersionProps.get('version')}
            ${quotationVersionProps.get('status')}
            ${quotationVersionProps.get('description')}
            ${quotationVersionProps.get('date')}
            ${quotationVersionProps.get('dueDate')}
            ${quotationVersionProps.get('acceptanceDate')}
            ${quotationVersionProps.get('paymentTerm')}
            ${quotationVersionProps.get('vatLevelId')}
            ${quotationVersionProps.get('downloadUriPDF')}
            ${quotationVersionProps.get('downloadUriExcel')}
            ${quotationVersionProps.get('rowsCount')}
            ${quotationVersionProps.get('rowsMigratedCount')}
            ${quotationVersionProps.get('fullyMigrated')}
            ${quotationVersionProps.get('exclVatTotal')}
            ${quotationVersionProps.get('inclVatTotal')}
            ${quotationVersionProps.get('discount')}
            ${quotationVersionProps.get('isNew')}
            ${quotationVersionProps.get('canBeSigned')}
            ${quotationVersionProps.get('quotation')} {
              ${quotationProps.get('id')}
              ${quotationProps.get('name')}
              ${quotationProps.get('projectId')}
            }
          `
          ),
          'quotationVersionById',
          this._graphEndpoint.clientName
        )
        .pipe(filter(isNonNullable))
    );
  }

  public fetchQuotationVersionsByQuotation$(quotationId: string) {
    return this.versionsByQuotation.fetch(
      quotationId,
      this._graphService
        .fetchQueryBuilder<QuotationVersion[]>(
          GET_QUOTATION_VERSIONS_BY_QUOTATION(
            quotationId,
            `
          ${quotationVersionProps.get('id')}
          ${quotationVersionProps.get('version')}
          ${quotationVersionProps.get('status')}
          ${quotationVersionProps.get('date')}
          ${quotationVersionProps.get('dueDate')}
          ${quotationVersionProps.get('paymentTerm')}
          ${quotationVersionProps.get('hideRowsPrice')}
          ${quotationVersionProps.get('exclVatTotal')}
          ${quotationVersionProps.get('inclVatTotal')}
          `
          ),
          this._graphEndpoint.clientName
        )
        .pipe(map(versions => versions ?? []))
    );
  }

  public getVersions(ids: string[]) {
    return combineLatest(ids.map(id => this.version.get$(id)));
  }

  public fetchQuotationParentRows$(versionId: string) {
    return this.quotationParentRows.fetch(
      versionId,
      this._graphService
        .fetchPagedQueryList<QuotationRow>(
          GET_QUOTATION_ROWS(
            `where: { parent: { eq: null }, quotationVersionId: { eq: "${versionId}" } }`,
            quotationRowSelection
          ),
          'filteredQuotationRows',
          this._graphEndpoint.clientName
        )
        .pipe(
          map(list => (list.items ? list.items : [])),
          map(list =>
            list.map(item => ({
              ...item,
              ...{ label: item.index.toString() },
            }))
          )
        )
    );
  }

  public fetchQuotationRowChildren$(parentId: string, parent?: QuotationRow) {
    return this.quotationParentRows.fetch(
      parentId,
      this._graphService
        .fetchPagedQueryList<QuotationRow>(
          GET_QUOTATION_ROWS(`where: { parent: { eq: "${parentId}" }}`, quotationRowSelection),
          'filteredQuotationRows',
          this._graphEndpoint.clientName
        )
        .pipe(
          map(list => (list.items ? list.items : [])),
          map(list =>
            list.map(item => ({
              ...item,
              ...{ label: `${parent?.label}.${item.index}` },
            }))
          )
        )
    );
  }

  public createQuotation$(payload: QuotationCreationPayloadInput) {
    return this._graphService
      .mutateBuilder<string>(CREATE_QUOTATION(payload), this._graphEndpoint.clientName)
      .pipe(filter(isNonNullable));
  }

  public createQuotationVersion$(payload: QuotationVersionCreationPayloadInput) {
    return this._graphService
      .mutateBuilder<string>(CREATE_QUOTATION_VERSION(payload), this._graphEndpoint.clientName, [
        'filteredQuotations',
        'quotationVersions',
        'quotationVersionsByQuotation',
      ])
      .pipe(filter(isNonNullable));
  }

  public updateQuotation$(payload: QuotationModifierPayloadInput) {
    const { quotationId, ...changes } = payload;
    // departmentId is not on the cached entity and updatedFields is mutation metadata.
    const { departmentId: _departmentId, updatedFields: _updatedFields, ...cacheChanges } = changes;
    return this._graphService
      .mutateBuilder<string>(
        UPDATE_QUOTATION({ ...payload, ...graphQlUpdateFields(changes) }),
        this._graphEndpoint.clientName
      )
      .pipe(
        filter(isNonNullable),
        tap(() => this.quotation.update(quotationId, cacheChanges))
      );
  }

  public updateQuotationVersion$(payload: QuotationVersionModifierPayloadInput) {
    const { quotationVersionId, ...changes } = payload;
    return this._graphService
      .mutateBuilder<string>(
        UPDATE_QUOTATION_VERSION({ ...payload, ...graphQlUpdateFields(changes) }),
        this._graphEndpoint.clientName,
        ['quotationVersions']
      )
      .pipe(
        filter(isNonNullable),
        switchMap(() => this.fetchQuotationVersion$(quotationVersionId))
      );
  }

  public setQuotationVersionSigned$(quotationVersionId: string) {
    return this._graphService
      .mutateBuilder<QuotationVersion>(QUOTATION_VERSION_SIGNED(quotationVersionId), this._graphEndpoint.clientName)
      .pipe(tap(version => this.version.update(quotationVersionId, version)));
  }

  public setQuotationVersionRefused$(quotationVersionId: string, refusalInfos: RefusalInfos) {
    return this._graphService
      .mutateBuilder<QuotationVersion>(
        QUOTATION_VERSION_REFUSED(quotationVersionId, refusalInfos),
        this._graphEndpoint.clientName
      )
      .pipe(tap(version => this.version.update(quotationVersionId, version)));
  }

  public isRead$(id: string) {
    return this._graphService.mutateBuilder<boolean>(READ_QUOTATION_VERSION(id), this._graphEndpoint.clientName);
  }

  public getSignatureLink$(quotationVersionId: string, successUri: string, errorUri: string, declineUri: string) {
    return this.quotationSignature.fetch(
      quotationVersionId,
      this._graphService.fetchQuery<QuotationSignature>(
        GET_QUOTATION_SIGNATURE_LINK(quotationVersionId, successUri, errorUri, declineUri),
        'signatureLink',
        this._graphEndpoint.clientName
      )
    );
  }

  public getVersionsLightInfo$(ids: string[]) {
    return this._graphService
      .fetchPagedQueryList<QuotationVersion>(
        GET_QUOTATION_VERSIONS(
          `where: { id: { in: [${ids.map(id => `"${id}"`).join(', ')}] } }`,
          `
            ${quotationVersionProps.get('id')}
            ${quotationVersionProps.get('version')}
            ${quotationVersionProps.get('quotation')} {
              ${quotationProps.get('name')}
              ${quotationProps.get('id')}
            }
          `
        ),
        'quotationVersions',
        this._graphEndpoint.clientName
      )
      .pipe(map(data => data.items ?? []));
  }

  public fetchVersionTenantRoute$(versionId: string) {
    return this.tenantRoute.fetch(
      this._graphService
        .fetchQuery<string>(
          GET_VERSION_TENANT_ROUTE(versionId),
          'quotationVersionTenantRoute',
          this._graphEndpoint.clientName
        )
        .pipe(filter(isNonNullable))
    );
  }
}
